import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { db } from '../firebase/config';
import { collection, doc, documentId, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useConfig } from '../contexts/ConfigContext';

const formatCurrency = (amount) => {
  const value = Number(amount || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  try {
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    }
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_) {
    return '';
  }
};

const mapPostedAtToMillis = (postedAt) => {
  if (!postedAt) return 0;
  const date = new Date(`${postedAt}T00:00:00Z`);
  const ms = date.getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

const normalizeCountryCode = (value = '') => value.trim().toUpperCase();

const fallbackTransportFees = {
  AM: { rate: 2.0, name: 'Armenia' },
  US: { rate: 0.0, name: 'United States' },
  GB: { rate: 1.5, name: 'United Kingdom' },
  DE: { rate: 1.0, name: 'Germany' },
  FR: { rate: 1.0, name: 'France' },
  IN: { rate: 3.0, name: 'India' },
  CN: { rate: 2.5, name: 'China' },
  default: { rate: 1.5, name: 'Other' },
};

const getTransportFeeDetails = (countryCode, countryOptions = []) => {
  const normalizedCode = normalizeCountryCode(countryCode || '');

  if (!normalizedCode) {
    return { percent: 0, label: 'Unknown', code: '' };
  }

  const matchedCountry = countryOptions.find((country) => normalizeCountryCode(country.code) === normalizedCode);

  if (matchedCountry) {
    return {
      percent: Number(matchedCountry.transportPercent) || 0,
      label: matchedCountry.name || normalizedCode,
      code: normalizedCode,
    };
  }

  const fallback = fallbackTransportFees[normalizedCode] || fallbackTransportFees.default;

  return {
    percent: fallback?.rate ? Number(fallback.rate) : 0,
    label: fallback?.name || normalizedCode,
    code: normalizedCode,
  };
};

const partnerSubAccountLabels = {
  lead: 'Lead',
  sales: 'Sales',
  support: 'Sales Support',
  client_pm: 'Client Relations',
  team_pm: 'Team Project Manager',
  administration: 'Administration',
  company: 'Company',
};

const resolveSubAccountLabel = (accountType, subAccount, overrides = {}) => {
  if (subAccount === null || subAccount === undefined || subAccount === '') return '—';
  const normalized = typeof subAccount === 'string' ? subAccount : String(subAccount);
  const loweredType = typeof accountType === 'string' ? accountType.toLowerCase() : '';
  if (loweredType === 'partner' && partnerSubAccountLabels[normalized]) {
    return partnerSubAccountLabels[normalized];
  }
  if (overrides[normalized]) {
    return overrides[normalized];
  }
  return normalized;
};

// Sub-account key of a ledger row: the sub-account entity it was posted to
// (partner roll-up), otherwise its subAccount attribution.
const getRowSubAccountKey = (row, entityId) => {
  if (row?.directAccountId && row.directAccountId !== entityId) return String(row.directAccountId);
  const raw = row?.subAccount;
  if (raw === null || raw === undefined || raw === '') return '';
  return typeof raw === 'string' ? raw : String(raw);
};

export default function PublicVendorLedger() {
  const { entityId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entity, setEntity] = useState(null);
  const [entries, setEntries] = useState([]);
  const [contractNames, setContractNames] = useState({});
  const [showSubAccounts, setShowSubAccounts] = useState(false);
  const [subAccountNameById, setSubAccountNameById] = useState({});
  const { getCountryOptions } = useConfig();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        setContractNames({});

        const entityRef = doc(db, 'entities', entityId);
        const entitySnap = await getDoc(entityRef);

        if (!entitySnap.exists()) {
          setError('Vendor not found.');
          return;
        }

        const entityData = entitySnap.data();

        if (!entityData.vendorPortalEnabled || !entityData.vendorPortalToken || entityData.vendorPortalToken !== token) {
          setError('This UMA Vendor Page link is invalid or has been disabled.');
          return;
        }

        setEntity({ id: entitySnap.id, ...entityData });

        // Partner pages roll up their sub-account entities (sub accounts and
        // employees parented to the partner) alongside the partner's own ledger.
        const accountIds = [entitySnap.id];
        if (entityData.entityType === 'partner') {
          const childNames = {};
          const childSnaps = await Promise.all([
            getDocs(query(collection(db, 'entities'), where('parentAccountId', '==', entitySnap.id))),
            getDocs(query(collection(db, 'entities'), where('parentEntityId', '==', entitySnap.id))),
          ]);
          childSnaps.forEach((snap) => snap.docs.forEach((childDoc) => {
            const data = childDoc.data() || {};
            childNames[childDoc.id] = data.entityName || data.name || childDoc.id;
          }));
          accountIds.push(...Object.keys(childNames).filter((id) => id !== entitySnap.id));
          setSubAccountNameById((prev) => ({ ...prev, ...childNames }));
        }

        const rows = [];
        for (let i = 0; i < accountIds.length; i += 10) {
          const ledgerSnap = await getDocs(query(
            collection(db, 'ledger'),
            where('directAccountId', 'in', accountIds.slice(i, i + 10))
          ));
          ledgerSnap.docs.forEach((docSnap) => rows.push({ id: docSnap.id, ...docSnap.data() }));
        }
        setEntries(rows);

        const contractIds = [...new Set(rows.map((row) => row.contractId).filter(Boolean))];
        const names = {};
        for (let i = 0; i < contractIds.length; i += 10) {
          try {
            const contractSnap = await getDocs(query(
              collection(db, 'contracts'),
              where(documentId(), 'in', contractIds.slice(i, i + 10))
            ));
            contractSnap.docs.forEach((contractDoc) => {
              names[contractDoc.id] = String(contractDoc.data().title || '').trim() || 'Untitled contract';
            });
          } catch (err) {
            console.error('Failed to load vendor ledger contract names', err);
          }
        }
        setContractNames(names);
      } catch (err) {
        console.error('Failed to load UMA Vendor Page', err);
        setError('We were unable to load this UMA Vendor Page.');
      } finally {
        setLoading(false);
      }
    };

    if (entityId && token) {
      load();
    }
  }, [entityId, token]);

  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      const postedDiff = mapPostedAtToMillis(b.postedAt) - mapPostedAtToMillis(a.postedAt);
      if (postedDiff !== 0) return postedDiff;

      const createdA = a.createdAt?.seconds ? a.createdAt.seconds * 1000 + (a.createdAt.nanoseconds || 0) / 1e6 : 0;
      const createdB = b.createdAt?.seconds ? b.createdAt.seconds * 1000 + (b.createdAt.nanoseconds || 0) / 1e6 : 0;
      return createdB - createdA;
    });
  }, [entries]);

  const balance = useMemo(() => {
    return sortedEntries.reduce((sum, row) => sum + Number(row.amountUsd || 0), 0);
  }, [sortedEntries]);

  const countryOptions = getCountryOptions();

  const transportFeeDetails = useMemo(() => {
    return getTransportFeeDetails(entity?.country, countryOptions);
  }, [entity?.country, countryOptions]);

  const transportFeeAmount = useMemo(() => {
    if (!entity) return 0;
    const percent = Number(transportFeeDetails.percent) || 0;
    if (percent <= 0) return 0;
    if (!Number.isFinite(balance) || balance <= 0) return 0;
    const raw = (balance * percent) / 100;
    return Math.round((raw + Number.EPSILON) * 100) / 100;
  }, [balance, entity, transportFeeDetails.percent]);

  const adjustedBalance = useMemo(() => {
    return balance + transportFeeAmount;
  }, [balance, transportFeeAmount]);

  const hasTransportFee = transportFeeAmount !== 0;

  const latestPostedAt = sortedEntries[0]?.postedAt || null;

  const entityAccountType = (entity?.entityType || entity?.accountType || '').toString().toLowerCase();

  useEffect(() => {
    if (!showSubAccounts) return;
    const candidateIds = Array.from(new Set(
      entries.map((row) => getRowSubAccountKey(row, entity?.id)).filter(Boolean)
    ));
    const missing = candidateIds
      .filter((id) => !partnerSubAccountLabels[id])
      .filter((id) => !subAccountNameById[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      try {
        for (let i = 0; i < missing.length; i += 10) {
          const chunk = missing.slice(i, i + 10);
          try {
            const snap = await getDocs(query(collection(db, 'entities'), where(documentId(), 'in', chunk)));
            snap.docs.forEach((docSnap) => {
              const data = docSnap.data() || {};
              updates[docSnap.id] = data.entityName || data.name || data.legalName || data.displayName || docSnap.id;
            });
          } catch (err) {
            chunk.forEach((id) => {
              if (!updates[id]) {
                updates[id] = id;
              }
            });
          }
        }
      } finally {
        if (!cancelled && Object.keys(updates).length > 0) {
          setSubAccountNameById((prev) => ({ ...prev, ...updates }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [showSubAccounts, entries, subAccountNameById, entity?.id]);

  const isPartnerPage = entityAccountType === 'partner';

  // Partner pages: balance per sub-account (rows without one count as "Main account")
  const subAccountBalances = useMemo(() => {
    if (!isPartnerPage) return [];
    const totals = new Map();
    entries.forEach((row) => {
      const key = getRowSubAccountKey(row, entity?.id);
      totals.set(key, (totals.get(key) || 0) + Number(row.amountUsd || 0));
    });
    return [...totals.entries()]
      .map(([key, total]) => ({
        key,
        label: key ? resolveSubAccountLabel('partner', key, subAccountNameById) : 'Main account',
        total,
      }))
      .sort((a, b) => (a.key === '' ? -1 : b.key === '' ? 1 : a.label.localeCompare(b.label)));
  }, [isPartnerPage, entries, entity?.id, subAccountNameById]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!entity) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <BusinessIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h4" fontWeight="bold">
          {entity.entityName}
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={`Status: ${(entity.status || 'unknown').toUpperCase()}`}
            size="small"
            color={entity.status === 'active' ? 'success' : entity.status === 'inactive' ? 'warning' : 'default'}
            variant="outlined"
          />
          <Chip
            label={`Account Type: ${(entity.accountType || 'direct').toUpperCase()}`}
            size="small"
            variant="outlined"
          />
          {entity.country && (
            <Chip label={`Country: ${entity.country}`} size="small" variant="outlined" />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          A read-only view of your UMA ledger with UCENA.
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }} elevation={3}>
        <Typography variant="h6" gutterBottom>
          Current Balance
        </Typography>
        <Typography
          variant="h3"
          fontWeight="bold"
          color={adjustedBalance >= 0 ? 'success.main' : 'error.main'}
          sx={{ lineHeight: 1.2 }}
        >
          {adjustedBalance >= 0 ? '+' : ''}{formatCurrency(adjustedBalance)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {adjustedBalance >= 0
            ? 'Positive balances indicate the amount available for you to invoice UCENA.'
            : 'Negative balances indicate payments received from UCENA.'}
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Ledger balance: {balance >= 0 ? '+' : ''}{formatCurrency(balance)}
          </Typography>
          {hasTransportFee && (
            <Typography variant="body2" color="text.secondary">
              Transport fee ({transportFeeDetails.percent}% for {transportFeeDetails.label}):
              {' '}
              {transportFeeAmount >= 0 ? '+' : ''}{formatCurrency(transportFeeAmount)}
            </Typography>
          )}
        </Box>
        {hasTransportFee && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Total shown above includes the transport fee applied for your country.
          </Typography>
        )}
        {(entity.email || entity.phone || entity.taxId) && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {entity.email && (
              <Typography variant="body2">📧 {entity.email}</Typography>
            )}
            {entity.phone && (
              <Typography variant="body2">📞 {entity.phone}</Typography>
            )}
            {entity.taxId && (
              <Typography variant="body2">Tax ID: {entity.taxId}</Typography>
            )}
          </Box>
        )}
        {latestPostedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Latest posting date: {latestPostedAt}
          </Typography>
        )}
      </Paper>

      {isPartnerPage && showSubAccounts && subAccountBalances.length > 0 && (
        <Paper sx={{ mb: 3 }} elevation={2}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Balance by Subaccount</Typography>
            <Typography variant="body2" color="text.secondary">
              Ledger balance split across your sub-accounts (before transport fee).
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Subaccount</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subAccountBalances.map((item) => (
                  <TableRow key={item.key || 'main'}>
                    <TableCell>{item.label}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color={item.total >= 0 ? 'success.main' : 'error.main'}>
                        {item.total >= 0 ? '+' : ''}{formatCurrency(item.total)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Paper sx={{ display: 'flex', flexDirection: 'column' }} elevation={2}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6">Ledger History</Typography>
            <Typography variant="body2" color="text.secondary">
              Entries are shown with the most recent activity first.
            </Typography>
          </Box>
          <FormControlLabel
            control={(
              <Switch
                checked={showSubAccounts}
                onChange={(event) => setShowSubAccounts(event.target.checked)}
                size="small"
              />
            )}
            label="Show Subaccounts"
          />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Event</TableCell>
                {showSubAccounts && <TableCell>Subaccount</TableCell>}
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showSubAccounts ? 6 : 5} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      No ledger entries have been posted yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedEntries.map((row) => {
                  const sourceLabel = row.source === 'Banking-E' ? 'Banking' : row.source;
                  const eventLabel = row.eventType === 'Bank-E' ? 'Banking' : row.eventType;
                  const createdLabel = formatTimestamp(row.createdAt);
                  const contractName = row.contractId ? (contractNames[row.contractId] || 'Contract unavailable') : '';
                  const memo = String(row.memo || '').trim();
                  const serviceName = String(row.serviceName || '').trim();
                  const description = contractName || memo || serviceName || '—';
                  const descriptionDetail = contractName
                    ? serviceName || memo
                    : (memo && serviceName !== memo ? serviceName : '');
                  const normalizedSubAccount = getRowSubAccountKey(row, entity.id);
                  const subAccountLabel = showSubAccounts
                    ? resolveSubAccountLabel(row.directAccountType || entityAccountType, normalizedSubAccount, subAccountNameById)
                    : '—';
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {row.postedAt || '—'}
                        </Typography>
                        {createdLabel && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Added {createdLabel}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={sourceLabel || '—'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={eventLabel || '—'} size="small" color="info" variant="outlined" />
                      </TableCell>
                      {showSubAccounts && (
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography variant="body2" color="text.secondary">
                              {subAccountLabel}
                            </Typography>
                            {/* {normalizedSubAccount && subAccountLabel !== normalizedSubAccount && (
                              <Typography variant="caption" color="text.secondary">
                                ID: {normalizedSubAccount}
                              </Typography>
                            )} */}
                          </Box>
                        </TableCell>
                      )}
                      <TableCell sx={{ overflowWrap: 'anywhere' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2">
                            {description}
                          </Typography>
                          {descriptionDetail && (
                            <Typography variant="caption" color="text.secondary">
                              {descriptionDetail}
                            </Typography>
                          )}
                          {/* {showSubAccounts && normalizedSubAccount && (
                            <Typography variant="caption" color="text.secondary">
                              Sub-account ID: {normalizedSubAccount}
                            </Typography>
                          )} */}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight="bold"
                          color={Number(row.amountUsd || 0) >= 0 ? 'success.main' : 'error.main'}
                        >
                          {Number(row.amountUsd || 0) >= 0 ? '+' : ''}{formatCurrency(row.amountUsd)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <Box sx={{ p: 2, textAlign: 'right' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Total balance (incl. transport):&nbsp;
            <Box component="span" color={adjustedBalance >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
              {adjustedBalance >= 0 ? '+' : ''}{formatCurrency(adjustedBalance)}
            </Box>
          </Typography>
          {hasTransportFee && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Transport component: {transportFeeAmount >= 0 ? '+' : ''}{formatCurrency(transportFeeAmount)}
            </Typography>
          )}
        </Box>
      </Paper>
    </Container>
  );
}
